import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { abbrevNum } from "../lib/elementStyle";
import { Twitch, Loader2, Eye, Heart, Trash2, AtSign } from "lucide-react";
import { t } from "../i18n";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 bg-black/40 border border-border rounded-sm px-3 py-2">
      <Icon className="h-4 w-4 text-[#9146FF]" />
      <div className="flex flex-col">
        <span className="text-[10px] text-muted-foreground">{label}</span>
        <span className="text-sm font-mono font-bold" data-testid={`tw-stat-${label}`}>{value}</span>
      </div>
    </div>
  );
}

export function TwitchModal({ open, onOpenChange, overlayId, config, setConfig, data, setData }) {
  const [channel, setChannel] = useState(config?.login || "");
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    if (!channel.trim()) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/overlays/${overlayId}/twitch/connect`, { channel: channel.trim() });
      setConfig(r.twitch);
      setData(r.data);
      toast.success(t("Canal {nome} conectado", { nome: r.twitch.display_name }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Falha ao conectar canal"));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    try {
      await api.post(`/overlays/${overlayId}/twitch/disconnect`);
      setConfig({ connected: false });
      setData(null);
      toast.success(t("Canal desconectado"));
    } catch { toast.error(t("Falha ao desconectar")); }
  };

  const connected = !!config?.connected;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="twitch-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Twitch className="h-5 w-5 text-white" /> {t("Dados da Twitch ao vivo")}
          </DialogTitle>
          <DialogDescription>
            {t("Conecte um canal para usar espectadores ao vivo e seguidores nos elementos dinâmicos do overlay.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label className="text-xs text-muted-foreground flex items-center gap-1.5"><AtSign className="h-3.5 w-3.5" /> {t("Nome do canal (ou link twitch.tv/canal)")}</label>
          <div className="flex gap-2">
            <input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder={t("ex: gaules")}
              onKeyDown={(e) => { if (e.key === "Enter") connect(); }}
              className="flex-1 h-9 bg-input border border-border rounded-sm px-2 text-xs outline-none focus:border-[#9146FF]" data-testid="tw-channel-input" />
            <Button onClick={connect} disabled={busy} size="sm" className="rounded-sm h-9 bg-[#9146FF] hover:bg-[#7c3aed] text-white" data-testid="tw-connect-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Conectar")}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">{t("Não precisa de login nem de chave — basta o nome do canal.")}</p>
        </div>

        {connected && (
          <div className="space-y-2 pt-1" data-testid="tw-live-preview">
            <div className="flex items-center justify-between">
              <span className="text-xs flex items-center gap-2 min-w-0">
                {config.profile_image && <img src={config.profile_image} alt="" className="h-5 w-5 rounded-full" />}
                <span className="truncate font-semibold">{config.display_name}</span>
              </span>
              <span className={`text-[10px] flex items-center gap-1 ${data?.isLive ? "text-[#9146FF]" : "text-muted-foreground"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${data?.isLive ? "bg-[#9146FF] animate-pulse" : "bg-muted-foreground"}`} /> {data?.isLive ? t("AO VIVO") : "offline"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat icon={Eye} label={t("Espectadores")} value={abbrevNum(data?.viewers || 0)} />
              <Stat icon={Heart} label={t("Seguidores")} value={abbrevNum(data?.followers || 0)} />
            </div>
            <Button onClick={disconnect} variant="destructive" size="sm" className="w-full rounded-sm h-8" data-testid="tw-disconnect-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t("Desconectar canal")}
            </Button>
            <p className="text-[10px] text-muted-foreground">{t("Os dados atualizam automaticamente e refletem em tempo real nos elementos dinâmicos do overlay.")}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
